import React from "react";
import { Alert, Box } from "@mui/material";
import AutoCompleteInput from "./AutoCompleteInput";
import TextInput from "./TextInput";
import InputHeader from "./InputBoxHeader";
import SelectOneInput from "./SelectOneInput";
import AddMultipleInput from "./AddMultipleInput";
import NumberInput from "./NumberInput";
import FileInput from "./FileInput";
import AddSingleInput from "./AddSingleInput";
import AddMultipleInputTable from "./AddMultipleInputTable";
import Form from "./Form";
import { useTranslation } from "react-i18next";

export default function FormGenerator({
                                          parentSection,
                                          fields,
                                          values,
                                          onChange,
                                          reportValidity,
                                          hiddenMap,
                                          disabledMap,
                                          info,
                                      }) {
    const sectionValues = (values && values[parentSection]) || {};
    const hiddenSet = hiddenMap?.[parentSection];

    return (
        <Box key="form-gen">
            {fields
                .filter((field) => !hiddenSet?.has(field.id))
                .map((field, index) => {
                const fieldValue =
                    sectionValues[field.id] !== undefined
                        ? sectionValues[field.id]
                        : defaultValueFor(field.type);

                return (
                    <Box key={field.id} sx={{ my: 5 }}>
                        <FieldRenderer
                            field={field}
                            value={fieldValue}
                            values={values}
                            onChange={onChange}
                            index={index + 1}
                            parentSection={parentSection}
                            sectionValues={sectionValues}
                            reportValidity={reportValidity}
                            disabledMap={disabledMap}
                            info={info}
                        />
                    </Box>
                );
            })}
        </Box>
    );
}

const componentMap = {
    autocomplete: AutoCompleteInput,
    text: TextInput,
    selectOne: SelectOneInput,
    addMultiple: AddMultipleInput,
    addMultipleTable: AddMultipleInputTable,
    addSingle: AddSingleInput,
    number: NumberInput,
    attachFile: FileInput,
    form: Form,
};

const defaultValueFor = (type) => {
    if (type === "addMultiple" || type === "addMultipleTable") return [];
    if (type === "addSingle") return {};
    if (type === "number") return "";
    return "";
};

const isEmpty = (v) =>
    v === undefined ||
    v === null ||
    (typeof v === "string" && v.trim() === "") ||
    (Array.isArray(v) && v.length === 0);

const validateField = (field, value) => {
    const required = field?.mandatory || field?.required;
    if (!required) return { valid: true, msg: null };

    switch (field.type) {
        case "form":
            return { valid: true, msg: null };
        case "number":
            return {
                valid: !(value === "" || value === null || value === undefined),
                msg: "Required",
            };
        case "autocomplete":
            return { valid: !!(value && (value.id || value.label)), msg: "Required" };
        case "selectOne":
            return { valid: !isEmpty(value), msg: "Required" };
        default:
            return { valid: !isEmpty(value), msg: "Required" };
    }
};

export function FieldRenderer({
                                  field,
                                  value,
                                  values,
                                  onChange,
                                  index,
                                  parentSection,
                                  childSection,
                                  sectionValues,
                                  reportValidity,
                                  keyPath,
                                  disabledMap,
                                  info,
                              }) {
    const { t } = useTranslation();
    const Component = componentMap[field.type];
    const idForValidity = keyPath || field.id;
    const allowCustomOption = field.allowCustomOption || false;

    const [errorMsg, setErrorMsg] = React.useState(null);

    // NEW: only show error after user interacted, but still report validity on load
    const [touched, setTouched] = React.useState(false);

    // NEW: wrapper ref to detect when focus actually leaves the whole field
    const wrapperRef = React.useRef(null);

    // Keep latest value for blur-time validation
    const latestValueRef = React.useRef(value);
    React.useEffect(() => {
        latestValueRef.current = value;
    }, [value]);

    const disabled = !!(
        disabledMap?.[parentSection]?.has(field.id) ||
        field.readOnly ||
        field.disabled
    );

    const computeValidity = React.useCallback(
        (val) => {
            if (disabled) return { valid: true, msg: null };
            return validateField(field, val);
        },
        [field, disabled]
    );

    // ✅ Always report validity (so Next button gating works on page load)
    // ✅ Only show error message after touched
    React.useEffect(() => {
        const { valid, msg } = computeValidity(value);

        reportValidity?.(parentSection, idForValidity, valid, msg);

        if (!touched) {
            setErrorMsg(null);
        } else {
            setErrorMsg(valid ? null : msg);
        }
    }, [value, touched, computeValidity, reportValidity, parentSection, idForValidity]);

    const handleFocusCapture = () => {
        if (disabled) return;
        if (!touched) setTouched(true);
    };

    const handleBlurCapture = () => {
        if (disabled) return;

        // Defer so MUI can finish selection/update before we validate
        setTimeout(() => {
            const el = wrapperRef.current;
            const active = document.activeElement;
            const stillInside = el && active && el.contains(active);

            if (!stillInside) {
                setTouched(true);
                const { valid, msg } = computeValidity(latestValueRef.current);
                setErrorMsg(valid ? null : msg);
                reportValidity?.(parentSection, idForValidity, valid, msg);
            }
        }, 0);
    };

    const handleChange = (fieldId, newValue) => {
        if (disabled) return;

        onChange(fieldId, newValue, parentSection);

        // If this field already touched, update error UI immediately on change
        if (touched && fieldId === field.id) {
            const { valid, msg } = computeValidity(newValue);
            setErrorMsg(valid ? null : msg);
            reportValidity?.(parentSection, idForValidity, valid, msg);
        }
    };

    if (!Component) return null;

    const componentProps = {
        field,
        value,
        values,
        index,
        onChange: handleChange,
        parentSection,
        childSection,
        error: Boolean(errorMsg),
        helperText: errorMsg || undefined,
        reportValidity,
        disabled,
        allowCustomOption,
    };

    if (field.type === "selectOne") {
        const selected = value;
        const chosen =
            field.fields?.find((opt) => String(opt.value) === String(selected)) || null;

        return (
            <Box
                ref={wrapperRef}
                onFocusCapture={handleFocusCapture}
                onBlurCapture={handleBlurCapture}
            >
                {index && <InputHeader field={field} index={index} />}
                <Component {...componentProps} />

                {chosen && !field.disabled ? (
                    <Box sx={{ pl: 4, borderLeft: "2px solid #eee", ml: 1, mt: 1 }}>
                        <FieldRenderer
                            field={chosen}
                            value={
                                sectionValues?.[chosen.id] !== undefined
                                    ? sectionValues[chosen.id]
                                    : defaultValueFor(chosen.type)
                            }
                            onChange={onChange}
                            parentSection={parentSection}
                            sectionValues={sectionValues}
                            reportValidity={reportValidity}
                            keyPath={`${field.id}::${chosen.id}`}
                            disabledMap={disabledMap}
                        />
                    </Box>
                ) : field.info ? (
                    <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
                        {info ? t(info) : t(field.info)}
                    </Alert>
                ) : null}
            </Box>
        );
    }

    if (field.type === "form") {
        return (
            <Box
                ref={wrapperRef}
                onFocusCapture={handleFocusCapture}
                onBlurCapture={handleBlurCapture}
            >
                {index && <InputHeader field={field} index={index} />}
                <Box sx={{ pl: 2, borderLeft: "2px solid #eee", ml: 1 }}>
                    {(field.fields || []).map((sub) => {
                        const subVal =
                            sectionValues?.[sub.id] !== undefined
                                ? sectionValues[sub.id]
                                : defaultValueFor(sub.type);

                        return (
                            <Box key={`${field.id}::${sub.id}`} sx={{ my: 3 }}>
                                <FieldRenderer
                                    field={sub}
                                    value={subVal}
                                    values={sectionValues}
                                    onChange={onChange}
                                    index={undefined}
                                    parentSection={parentSection}
                                    sectionValues={sectionValues}
                                    reportValidity={reportValidity}
                                    keyPath={`${field.id}::${sub.id}`}
                                    disabledMap={disabledMap}
                                />
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    }

    return (
        <Box
            ref={wrapperRef}
            onFocusCapture={handleFocusCapture}
            onBlurCapture={handleBlurCapture}
        >
            {index && <InputHeader field={field} index={index} />}
            <Component {...componentProps} />
        </Box>
    );
}
